import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Button,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

function VcfModal(props) {
    // console.log('props: ', props );
    const { vcfModalIsOpen, 
            setVcfModalIsOpen, 
            sampleNames, 
            selectedVcfSamples, 
            setSelectedVcfSamples, 
            selectedTracks, 
            setSelectedTracks } = props;
    const [sampleFilter, setSampleFilter] = useState("");

    const errorIndicator = "An error occurred_3"; // The specific error message to check for
    const [availableVcfSamples, setAvailableVcfSamples] = useState([]);

    // const [availableVcfSamples, setAvailableVcfSamples] = useState(
    //     sampleNames.includes(errorIndicator) ? [] : sampleNames
    // );

    const removeGffExtension = (sample) => sample.replace('.gff', '');

    useEffect(() => {
        // Initialize availableVcfSamples based on sampleNames and errorIndicator
        const initialAvailableSamples = sampleNames.includes(errorIndicator) ? [] : sampleNames;

        // Filter out selectedVcfSamples from initialAvailableSamples
        const filteredAvailableSamples = initialAvailableSamples.filter(
            sample => !selectedVcfSamples.map(removeGffExtension).includes(sample)
        );

        setAvailableVcfSamples(filteredAvailableSamples);
    }, [sampleNames, selectedVcfSamples]);

    // console.log("available samples: ", availableVcfSamples);
    // console.log("selected: ", selectedVcfSamples);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                closeModal();
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        // Cleanup the event listener when the component is unmounted
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    // State to hold dynamically added VCF samples
    const [dynamicVcfSamples, setDynamicVcfSamples] = useState([]);

    // Add all dynamic samples to both the dynamicVcfSamples state and the selectedTracks
    const selectVcfAll = () => {
        const newSamples = availableVcfSamples.filter(sample => !selectedTracks.includes(sample));  // Assuming sampleNames holds all available samples

        // Appending '.gff' to each sample in availableVcfSamples
        const updatedSamples = availableVcfSamples.map(sample => `${sample}.gff`);
        console.log("Updated VCF Samples (with .gff): ", updatedSamples);
        
        setDynamicVcfSamples(newSamples);
        setSelectedTracks([...selectedTracks, ...updatedSamples]);  // Merge with existing tracks
        setSelectedVcfSamples( availableVcfSamples );
    };

    // Remove all dynamic samples from both the dynamicVcfSamples state and the selectedTracks
    const removeVcfAll = () => {
        console.log("Before removal, selected tracks: ", selectedTracks);
        
        // Removing only the selected VCF samples with the `.gff` extension
        setSelectedTracks(prevSelectedTracks => 
            prevSelectedTracks.filter(track => {
                // Check if the track is in selectedVcfSamples (without '.gff')
                const trackWithoutExtension = track.replace('.gff', ''); 
                return !selectedVcfSamples.includes(trackWithoutExtension);
            })
        );
    
        // Reset dynamic samples and selected Vcf samples
        setDynamicVcfSamples([]);
        setSelectedVcfSamples([]);
    
        // Log the updated selectedTracks after a slight delay to capture the state update
        setTimeout(() => {
            console.log("After removal, selected tracks: ", selectedTracks);
        }, 2000);
    };

    const addSample = (sample) => {
        // Append ".gff" to the sample for consistency
        const sampleWithExtension = `${sample}.gff`;
    
        // Check and add to selectedVcfSamples if not already included
        setSelectedVcfSamples(prevSamples => {
            if (!prevSamples.includes(sampleWithExtension)) {
                return [...prevSamples, sampleWithExtension];
            } else {
                return prevSamples;
            }
        });
    
        // Check and add to selectedTracks if not already included
        setSelectedTracks(prevSelectedTracks => {
            if (!prevSelectedTracks.includes(sampleWithExtension)) {
                return [...prevSelectedTracks, sampleWithExtension];
            } else {
                return prevSelectedTracks;
            }
        });
    
        // Remove the sample from availableVcfSamples
        setAvailableVcfSamples(prevAvailableSamples => {
            return prevAvailableSamples.filter(item => item !== sample);
        });
    };
    
    const removeSample = (sample) => {
        console.log("remove sample: ", sample);
        // Append ".gff" to the sample for consistency
        const sampleWithExtension = `${sample}.gff`;
    
        // Filter out the sample from selectedVcfSamples
        setSelectedVcfSamples(prevSamples => prevSamples.filter(s => s !== sample ));
    
        // Correctly filter out the sample from selectedTracks
        setSelectedTracks(prevSelectedTracks => prevSelectedTracks.filter(track => track !== sampleWithExtension));
        
        // Add the sample back to availableVcfSamples
        setAvailableVcfSamples(prevAvailableSamples => [...prevAvailableSamples, sample]);
    };
    
    const closeModal = () => {
        setVcfModalIsOpen(false);
    };

    return (
        <Dialog
          open={vcfModalIsOpen}
          onClose={closeModal}
          fullWidth
          maxWidth="md"
          BackdropProps={{
            style: {
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              backdropFilter: 'blur(4px)'
            },
            className: 'bg-black bg-opacity-60 backdrop-blur-sm'
          }}
        >
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">Sample Tracks</Typography>
              <IconButton onClick={closeModal}>
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>

          <DialogContent>
            <Box marginBottom={2}>
              <Typography variant="h6" gutterBottom>
                Select VCF Sample Tracks
              </Typography>
            </Box>
            {
              availableVcfSamples.includes(errorIndicator) ? (
                <Typography align="center" color="textSecondary" gutterBottom>
                  No VCF Tracks Available
                </Typography>
              ) : (
                <Box display="flex" gap={4}>
                  <Box flex={1}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Available Samples
                    </Typography>
                    <Button
                      onClick={selectVcfAll}
                      variant="text"
                      color="primary"
                      sx={{ marginBottom: 2 }}
                    >
                      Select All
                    </Button>
                    <Box
                      sx={{
                        backgroundColor: '#f9f9f9',
                        borderRadius: 1,
                        p: 2,
                        maxHeight: 240,
                        overflowY: 'auto',
                      }}
                    >
                      {availableVcfSamples.filter(sample => !selectedVcfSamples.includes(sample) && sample !== errorIndicator).map(sample => (
                        <Box
                          key={sample}
                          sx={{
                            cursor: 'pointer',
                            p: 1,
                            display: 'flex',
                            alignItems: 'center',
                            '&:hover': { backgroundColor: '#e2e2e2' }
                          }}
                          onClick={() => addSample(sample)}
                        >
                          <i
                            className="fas fa-plus-circle"
                            style={{ color: '#1976d2', marginRight: '8px' }}
                          />
                          <Typography>{sample}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                  <Box flex={1}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Selected Samples
                    </Typography>
                    <Button
                      onClick={removeVcfAll}
                      variant="text"
                      color="error"
                      sx={{ marginBottom: 2 }}
                    >
                      Remove All
                    </Button>
                    <Box
                      sx={{
                        backgroundColor: '#f9f9f9',
                        borderRadius: 1,
                        p: 2,
                        maxHeight: 240,
                        overflowY: 'auto',
                      }}
                    >
                      {selectedVcfSamples.map(sample => (
                        <Box
                          key={sample}
                          sx={{
                            cursor: 'pointer',
                            p: 1,
                            display: 'flex',
                            alignItems: 'center',
                            '&:hover': { backgroundColor: '#e2e2e2' }
                          }}
                          onClick={() => removeSample(sample)}
                        >
                          <i
                            className="fas fa-minus-circle"
                            style={{ color: '#d32f2f', marginRight: '8px' }}
                          />
                          <Typography>{sample}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
              )
            }
          </DialogContent>

          <DialogActions>
            <Button
              variant="contained"
              onClick={closeModal}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
    );
}

export default VcfModal;
